<template>
    <div class="modal pca-modal" tabindex="-1" role="dialog" :id="modalId" :aria-labelledby="`${modalId}-title`"
        @keydown.esc.prevent="close">
        <div class="modal-dialog modal-dialog-centered pca-dialog" role="document">
            <div class="modal-content pca" v-if="ctx" :style="glowStyle">
                <header class="pca-head">
                    <button type="button" class="pca-close" :disabled="saving" @click="close">
                        <fa icon="times" />
                        <span class="sr-only">Fechar</span>
                    </button>

                    <!-- Muven e canal ligados pelo fio: o estado do fio acompanha a escolha -->
                    <div class="pca-bridge" :class="{'is-linked': !!selected}" aria-hidden="true">
                        <BrandTileCmp :size="40" :image="muvenMarkUrl" background="var(--brand-fill)" color="var(--text-on-brand)" />
                        <span class="pca-bridge-wire">
                            <span class="pca-bridge-node"><fa icon="link" /></span>
                        </span>
                        <BrandTileCmp :size="40" :image="channelMarkUrl" :icon="isCustom ? 'store-alt' : null"
                            :text="brand.monogram" :background="brand.color" :color="brand.textColor" />
                    </div>

                    <h2 class="pca-title" :id="`${modalId}-title`">
                        {{currentId ? 'Trocar associação' : 'Associar'}} com {{channelLabel}}
                    </h2>
                    <p class="pca-subtitle">
                        Escolha, {{inChannel}}, o produto que corresponde a este item do Muven.
                        O ID certo é montado para você.
                    </p>
                </header>

                <div class="pca-body">
                    <aside class="pca-muven">
                        <p class="pca-eyebrow">No Muven</p>

                        <div class="pca-muven-product">
                            <span class="pca-muven-thumb" :class="{'is-empty': !ctx.product.imageUrl}">
                                <img v-if="ctx.product.imageUrl" :src="ctx.product.imageUrl" alt="" />
                                <fa v-else icon="box" />
                            </span>
                            <p class="pca-muven-name">{{ctx.product.name}}</p>
                        </div>

                        <dl class="pca-muven-facts">
                            <div>
                                <dt>SKU</dt>
                                <dd><code>{{ctx.product.skuCode || '-'}}</code></dd>
                            </div>
                            <div v-if="ctx.product.codeSource">
                                <dt>Cód. retaguarda</dt>
                                <dd><code>{{ctx.product.codeSource}}</code></dd>
                            </div>
                            <div v-if="ctx.product.eanCode">
                                <dt>EAN</dt>
                                <dd><code>{{ctx.product.eanCode}}</code></dd>
                            </div>
                        </dl>

                        <div class="pca-today" :class="`is-${currentTone}`">
                            <p class="pca-eyebrow">Associado hoje</p>
                            <template v-if="currentId">
                                <code class="pca-today-id">{{currentId}}</code>
                                <p class="pca-today-state" v-if="currentStateText">
                                    <fa :icon="currentTone === 'ok' ? 'check-circle' : 'exclamation-triangle'" />
                                    {{currentStateText}}
                                </p>
                            </template>
                            <p v-else class="pca-today-empty">Sem associação com este canal.</p>
                            <p class="pca-today-siblings" v-if="siblingIds.length">
                                Este produto tem mais {{siblingIds.length}} {{siblingIds.length === 1 ? 'anúncio' : 'anúncios'}} neste canal:
                                <code v-for="id in siblingIds" :key="id">{{id}}</code>
                            </p>
                        </div>
                    </aside>

                    <section class="pca-channel">
                        <div class="pca-tabs" role="tablist" aria-label="Como encontrar o produto no canal"
                            v-if="searchProfile.supportsGetById">
                            <button type="button" role="tab" class="pca-tab" :class="{'is-active': tab === 'search'}"
                                :aria-selected="String(tab === 'search')" @click="switchTab('search')">
                                <fa icon="search" /> Buscar no canal
                            </button>
                            <button type="button" role="tab" class="pca-tab" :class="{'is-active': tab === 'id'}"
                                :aria-selected="String(tab === 'id')" @click="switchTab('id')">
                                <fa icon="barcode" /> Já tenho o ID
                            </button>
                        </div>

                        <form v-if="tab === 'search'" class="pca-search" @submit.prevent="searchFromInput">
                            <input ref="searchInput" v-model.trim="query" type="search" class="pca-input"
                                :placeholder="searchHint" :aria-label="`Buscar ${inChannel}: ${searchHint}`"
                                autocomplete="off" spellcheck="false" />
                            <button type="submit" class="pca-btn" :disabled="!query || searching">
                                <Loading v-if="searching" />
                                <template v-else>Buscar</template>
                            </button>
                        </form>

                        <form v-else class="pca-search" @submit.prevent="lookupId">
                            <input ref="idInput" v-model.trim="idQuery" type="text" class="pca-input is-mono"
                                :placeholder="idHint" aria-label="ID do produto no canal"
                                autocomplete="off" spellcheck="false" />
                            <button type="submit" class="pca-btn" :disabled="!idQuery || searching">
                                <Loading v-if="searching" />
                                <template v-else>Conferir</template>
                            </button>
                        </form>

                        <p class="pca-search-note" aria-live="polite">
                            <template v-if="tab === 'search'">
                                <template v-if="searchedLabel">
                                    Buscamos por <strong>{{searchedLabel}}</strong>
                                    <template v-if="searchedTerm">: “{{searchedTerm}}”</template>.
                                    <span v-if="fallbackNote" class="pca-fallback">{{fallbackNote}}</span>
                                </template>
                                <template v-else>O canal {{channelLabel}} aceita: {{searchHint}}.</template>
                            </template>
                            <template v-else>
                                Cole o ID que aparece {{inChannel}}. Nós conferimos e, se ele tiver variações, você escolhe a certa.
                            </template>
                        </p>

                        <div class="pca-results" aria-live="polite" :aria-busy="String(searching)">
                            <div v-if="searching" class="pca-skeletons" aria-hidden="true">
                                <span v-for="n in 3" :key="n" class="pca-skeleton"></span>
                            </div>

                            <div v-else-if="resultError" class="pca-empty is-error">
                                <fa icon="info-circle" class="pca-empty-icon" />
                                <p class="pca-empty-title">Não deu para consultar o canal agora</p>
                                <p class="pca-empty-text">{{resultError}}</p>
                                <button type="button" class="pca-link-btn" @click="retry">
                                    <fa icon="sync-alt" /> Tentar de novo
                                </button>
                            </div>

                            <div v-else-if="hasSearched && !groups.length" class="pca-empty">
                                <fa icon="search" class="pca-empty-icon" />
                                <p class="pca-empty-title">
                                    {{tab === 'id' ? 'Este ID não existe neste canal' : 'Nenhum produto encontrado'}}
                                </p>
                                <p class="pca-empty-text" v-if="tab === 'search'">
                                    <template v-if="emptyNote">{{emptyNote}} </template>
                                    Tente outro termo. O canal {{channelLabel}} aceita: {{searchHint}}.
                                </p>
                                <p class="pca-empty-text" v-else>Confira se o ID foi copiado inteiro.</p>
                                <button v-if="tab === 'search' && searchProfile.supportsGetById" type="button" class="pca-link-btn" @click="switchTab('id')">
                                    <fa icon="barcode" /> Tenho o ID do produto
                                </button>
                            </div>

                            <div v-else-if="!hasSearched && tab === 'search' && searchProfile.manualGuide" class="pca-empty is-guide">
                                <fa icon="info-circle" class="pca-empty-icon" />
                                <p class="pca-empty-title">Como achar o produto {{inChannel}}</p>
                                <p class="pca-empty-text">{{searchProfile.manualGuide}}</p>
                            </div>

                            <div v-else-if="!hasSearched" class="pca-empty is-idle">
                                <p class="pca-empty-text">
                                    {{tab === 'search' ? 'Digite um termo e busque para ver os produtos do canal.' : 'Informe o ID e confira.'}}
                                </p>
                            </div>

                            <template v-else>
                                <article v-for="group in groups" :key="group.key" class="pca-group"
                                    :class="{'has-variations': group.hasVariations}">
                                    <header class="pca-group-head">
                                        <div class="pca-group-text">
                                            <p class="pca-group-name">{{group.name || 'Produto sem nome'}}</p>
                                            <p class="pca-group-meta">
                                                <span>{{parentLabel}} <code>{{group.idExternalChannel}}</code></span>
                                                <span v-if="group.hasVariations">{{group.options.length}} {{group.options.length === 1 ? 'variação' : 'variações'}}</span>
                                                <span v-if="group.isActive === false" class="pca-tag is-muted">Inativo no canal</span>
                                            </p>
                                        </div>
                                    </header>

                                    <p class="pca-group-hint" v-if="group.hasVariations">
                                        Este {{parentLabel.toLowerCase()}} tem variações. Escolha a que corresponde a este produto.
                                    </p>

                                    <div class="pca-options" role="radiogroup" :aria-label="group.name">
                                        <label v-for="option in group.options" :key="option.key" class="pca-option"
                                            :class="{'is-selected': selected && selected.key === option.key, 'is-current': option.isCurrent, 'is-locked': isSibling(option.idToSave)}">
                                            <input type="radio" class="pca-option-radio" :name="`pca-option-${_uid}`"
                                                :value="option.key" :checked="selected && selected.key === option.key"
                                                :disabled="saving || isSibling(option.idToSave)" @change="choose(option)" />
                                            <span class="pca-option-mark" aria-hidden="true"></span>

                                            <span class="pca-option-main">
                                                <span class="pca-option-title">
                                                    <template v-if="option.attributes.length">
                                                        <span v-for="attr in option.attributes" :key="attr.name" class="pca-attr">
                                                            <small>{{attr.name}}</small>{{attr.value}}
                                                        </span>
                                                    </template>
                                                    <span v-else-if="option.isVariation" class="pca-attr">Variação</span>
                                                    <span v-else class="pca-attr is-plain">Produto sem variações</span>
                                                </span>
                                                <span class="pca-option-meta">
                                                    <span v-if="option.skuCode">SKU <code>{{option.skuCode}}</code></span>
                                                    <span v-if="option.isVariation">ID <code>{{option.idVariation}}</code></span>
                                                    <span v-if="option.isActive === false">Inativa</span>
                                                </span>
                                            </span>

                                            <span class="pca-option-badges">
                                                <span v-if="option.isCurrent" class="pca-tag is-brand">Associado hoje</span>
                                                <span v-else-if="isSibling(option.idToSave)" class="pca-tag is-muted">Já é outro anúncio deste produto</span>
                                                <span v-if="option.skuMatches" class="pca-tag is-ok">
                                                    <fa icon="check-circle" /> Mesmo código do Muven
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                </article>
                            </template>
                        </div>
                    </section>
                </div>

                <footer class="pca-foot" :class="`is-${footTone}`" aria-live="polite">
                    <template v-if="step === 'remove'">
                        <div class="pca-foot-text">
                            <p class="pca-foot-title">Remover a associação com {{channelLabel}}?</p>
                            <p class="pca-foot-sub" v-if="isMulti">
                                O anúncio <code>{{currentId}}</code> deixa de estar ligado a este produto e o card dele sai da lista.
                                Pedidos desse anúncio vão dar erro até uma nova associação.
                            </p>
                            <p class="pca-foot-sub" v-else>
                                O produto fica sem vínculo com <code>{{currentId}}</code>. Pedidos com ele vão dar erro
                                até uma nova associação.
                            </p>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="step = 'pick'">Voltar</button>
                            <button type="button" class="pca-btn is-danger" :disabled="saving" @click="isMulti ? removeMulti() : remove()">
                                <Loading v-if="saving" />
                                <template v-else>Remover associação</template>
                            </button>
                        </div>
                    </template>

                    <template v-else-if="step === 'confirm'">
                        <div class="pca-foot-text">
                            <p class="pca-foot-title">
                                <template v-if="pendingAction === 'add'">Adicionar <code>{{selected.idToSave}}</code> como outro anúncio deste produto?</template>
                                <template v-else-if="currentId">Trocar <code>{{currentId}}</code> por <code>{{selected.idToSave}}</code>?</template>
                                <template v-else>Associar ao ID <code>{{selected.idToSave}}</code>?</template>
                            </p>
                            <p class="pca-foot-sub" v-if="pendingAction === 'add'">
                                <code>{{currentId}}</code> continua associado. Os dois anúncios passam a apontar para este produto.
                            </p>
                            <p class="pca-foot-sub" v-else>Os próximos pedidos e atualizações deste produto {{inChannel}} passam a usar este ID.</p>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="step = 'pick'">Voltar</button>
                            <button type="button" class="pca-btn is-primary" ref="confirmButton" :disabled="saving" @click="isMulti ? saveMulti() : save()">
                                <Loading v-if="saving" />
                                <template v-else>Confirmar</template>
                            </button>
                        </div>
                    </template>

                    <template v-else>
                        <div class="pca-foot-text">
                            <template v-if="!selected">
                                <p class="pca-foot-sub">Escolha um produto do canal {{channelLabel}} para continuar.</p>
                            </template>
                            <template v-else-if="selected.idToSave === currentId">
                                <p class="pca-foot-sub">Este já é o produto associado hoje.</p>
                            </template>
                            <template v-else-if="isSibling(selected.idToSave)">
                                <p class="pca-foot-sub">Este anúncio já está associado a este produto em outro card.</p>
                            </template>
                            <template v-else>
                                <p class="pca-foot-label">Vai gravar</p>
                                <p class="pca-foot-id">
                                    <code>{{selected.idToSave}}</code>
                                    <span v-if="composedParts.length" class="pca-parts">
                                        <template v-for="(part, index) in composedParts">
                                            <span v-if="index" :key="`plus-${index}`" class="pca-parts-plus">+</span>
                                            <span :key="part.label" class="pca-part"><small>{{part.label}}</small>{{part.value}}</span>
                                        </template>
                                    </span>
                                </p>
                            </template>

                            <button v-if="currentId && (!isMulti || !!ctx.storeChannel.id)" type="button" class="pca-remove-link" :disabled="saving" @click="step = 'remove'">
                                <fa icon="trash-alt" /> Remover associação
                            </button>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="close">Cancelar</button>
                            <!-- Canal multi: o anúncio escolhido pode entrar no lugar deste ou somar a ele -->
                            <button v-if="isMulti && currentId" type="button" class="pca-btn is-ghost" :disabled="!canSave" @click="askConfirm('add')">
                                Adicionar como outro anúncio
                            </button>
                            <button type="button" class="pca-btn is-primary" :disabled="!canSave" @click="askConfirm(currentId ? 'swap' : 'associate')">
                                {{currentId ? 'Trocar associação' : 'Associar'}}
                            </button>
                        </div>
                    </template>

                    <p v-if="saveError" class="pca-foot-error">{{saveError}}</p>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { ChannelEnum } from '@c2b/muven-commons'
import productService from '@/product/services/ProductService'
import modalService from '@/utils/services/ModalService'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import {
    buildAssociationGroups,
    describeComposedId,
    getChannelSearchHint,
    getChannelSearchProfile,
    getSuggestedSearches,
    isNotFoundError,
    keepExactMatches,
} from '@/channel/utils/ChannelProductAssociationHelper'

// O endpoint multi devolve o produto com a lista de vínculos ({id, idExternalChannel}); acha o recém-criado
function findMultiAssociationId(response, idExternalChannel) {
    const list = (response && response[0] && response[0].idExternalChannelList) || []
    const found = list.find((item) => item && String(item.idExternalChannel) === String(idExternalChannel))

    return found ? found.id : null
}

/**
 * Associação guiada de um produto simples do Muven com um canal, no mesmo
 * espírito da tela "Associar produtos": busca no canal, escolha do produto ou
 * da variação e o ID montado sozinho (item_id+model_id, sufixo _V da Tray...).
 *
 *   const novoId = await this.$refs.associationModal.open({...})
 *   // string: associou/trocou | null: removeu | undefined: cancelou
 *
 * Com `multi: true` (Mercado Livre com vários anúncios por produto) cada card é
 * uma linha própria da product_store_channel e o retorno vira
 * {action: 'swap' | 'add' | 'associate' | 'remove', idExternalChannel, id}.
 * Não há endpoint que troque o id de uma linha, então trocar é criar a linha nova
 * e apagar a antiga. `siblingIds` são os outros anúncios do produto no canal.
 */
export default {
    name: 'ProductChannelAssociationModalCmp',
    components: {
        BrandTileCmp,
    },
    data() {
        return {
            ctx: null,
            tab: 'search',
            query: '',
            idQuery: '',
            searching: false,
            hasSearched: false,
            resultError: '',
            lastAction: null,
            searchedLabel: '',
            searchedTerm: '',
            fallbackNote: '',
            // Explica um "nada encontrado" que pode ser falha do canal (ver searchErrorMeansEmpty)
            emptyNote: '',
            groups: [],
            selected: null,
            step: 'pick',
            // Multi: o que o "Confirmar" vai fazer (swap | add | associate)
            pendingAction: 'swap',
            saving: false,
            saveError: '',
            resolver: null,
            // Respostas antigas não podem sobrescrever uma busca mais nova
            requestSeq: 0,
        }
    },
    computed: {
        modalId() {
            return `product-channel-association-${this._uid}`
        },
        enumChannel() {
            return this.ctx ? this.ctx.storeChannel.channel.enumChannel : null
        },
        channelLabel() {
            return this.ctx ? this.ctx.storeChannel.channel.name : ''
        },
        // "no canal" serve para qualquer nome, sem errar o gênero (na Shopee, no Mercado Livre)
        inChannel() {
            return `no canal ${this.channelLabel}`
        },
        channelId() {
            return this.ctx.storeChannel.idChannel || this.ctx.storeChannel.channel.id
        },
        brand() {
            return getChannelBrand(this.enumChannel)
        },
        channelMarkUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        muvenMarkUrl() {
            return require('@/channel/images/marks/muven-cart.png')
        },
        isCustom() {
            return this.enumChannel === ChannelEnum.Custom
        },
        glowStyle() {
            const glow = getChannelGlow(this.brand)

            return glow ? {'--pca-glow': glow} : {}
        },
        currentId() {
            return (this.ctx && this.ctx.storeChannel.idExternalChannel) || ''
        },
        isMulti() {
            return !!(this.ctx && this.ctx.multi)
        },
        siblingIds() {
            return this.isMulti ? (this.ctx.siblingIds || []).map((id) => String(id)) : []
        },
        currentTone() {
            const state = this.ctx && this.ctx.current ? this.ctx.current.state : ''

            if (state === 'found') {
                return 'ok'
            }

            if (state === 'notFound') {
                return 'bad'
            }

            if (state === 'parent') {
                return 'warn'
            }

            return 'neutral'
        },
        currentStateText() {
            return {
                ok: 'Confere no canal',
                bad: 'Não encontrado no canal',
                warn: 'É um produto pai: falta escolher a variação',
            }[this.currentTone] || ''
        },
        searchProfile() {
            return getChannelSearchProfile(this.enumChannel)
        },
        searchHint() {
            return getChannelSearchHint(this.enumChannel)
        },
        // Códigos do produto no Muven que o canal pode ter recebido (ver exportação de cada agente)
        muvenCodes() {
            return [this.ctx.product.skuCode, this.ctx.product.codeSource].filter((c) => !!c)
        },
        idHint() {
            return this.enumChannel === ChannelEnum.Shopee ? 'item_id ou item_id+model_id' : 'ID do produto no canal'
        },
        parentLabel() {
            return this.enumChannel === ChannelEnum.Shopee ? 'Anúncio' : 'Produto'
        },
        composedParts() {
            return this.selected ? describeComposedId(this.enumChannel, this.selected) : []
        },
        canSave() {
            return !!this.selected && this.selected.idToSave !== this.currentId && !this.isSibling(this.selected.idToSave) && !this.saving
        },
        footTone() {
            if (this.step === 'remove') {
                return 'danger'
            }

            if (this.step === 'confirm') {
                return 'confirm'
            }

            return this.canSave ? 'ready' : 'idle'
        },
    },
    methods: {
        /**
         * @param {{storeChannel, idStore, idMuven, product: {name, skuCode, codeSource, eanCode, imageUrl}, current}} ctx
         */
        open(ctx) {
            Object.assign(this.$data, this.$options.data.call(this))
            this.ctx = ctx

            return new Promise((resolve) => {
                this.resolver = resolve
                modalService.show(`#${this.modalId}`)
                this.$nextTick(() => this.autoSearch())
            })
        },
        async close() {
            if (this.saving) {
                return
            }

            await this.finish(undefined)
        },
        async finish(result) {
            await modalService.hideAndWait(`#${this.modalId}`)

            if (this.resolver) {
                this.resolver(result)
                this.resolver = null
            }

            this.ctx = null
        },
        focusInput() {
            setTimeout(() => {
                const input = this.tab === 'search' ? this.$refs.searchInput : this.$refs.idInput

                if (input) {
                    input.focus()
                }
            }, 250)
        },
        switchTab(tab) {
            if (this.tab === tab) {
                return
            }

            this.tab = tab
            this.groups = []
            this.selected = null
            this.hasSearched = false
            this.resultError = ''
            this.step = 'pick'
            this.focusInput()
        },
        // Ao abrir, busca sozinho pelo que o agente do canal sabe procurar e, se não achar, tenta o próximo termo
        async autoSearch() {
            const suggestions = getSuggestedSearches(this.enumChannel, this.ctx.product)

            if (!suggestions.length) {
                this.focusInput()
                return
            }

            for (let i = 0; i < suggestions.length; i++) {
                const suggestion = suggestions[i]
                this.query = suggestion.term

                const found = await this.runSearch(suggestion.term, suggestion.label)

                if (found || this.resultError || !this.ctx) {
                    if (i > 0 && found) {
                        this.fallbackNote = `Nada pelo ${suggestions[0].label.toLowerCase()}, então tentamos este.`
                    }
                    break
                }
            }

            this.focusInput()
        },
        searchFromInput() {
            this.fallbackNote = ''
            this.runSearch(this.query, '')
        },
        async runSearch(term, label) {
            const seq = ++this.requestSeq
            this.lastAction = () => this.runSearch(term, label)
            this.startLoading()
            this.searchedLabel = label || 'o termo digitado'
            this.searchedTerm = term

            try {
                const products = await productService.getAssociableProductInChannelByAnyInfo(this.ctx.idStore, this.channelId, term)

                if (seq !== this.requestSeq) {
                    return false
                }

                this.groups = this.toGroups(products || [], term)
            } catch (err) {
                if (seq !== this.requestSeq) {
                    return false
                }

                if (this.searchProfile.searchErrorMeansEmpty) {
                    this.emptyNote = 'O canal não encontrou o código ou não respondeu.'
                } else if (!isNotFoundError(err)) {
                    this.resultError = (err && err.message) || 'O canal não respondeu.'
                }
            }

            this.stopLoading()

            return !!this.groups.length
        },
        async lookupId() {
            const id = this.idQuery
            const seq = ++this.requestSeq
            this.lastAction = () => this.lookupId()
            this.startLoading()

            try {
                const product = await productService.getAssociableProductInChannelByIdExternalChannel(this.ctx.idStore, this.channelId, id)

                if (seq !== this.requestSeq) {
                    return
                }

                const exists = product && (product.name || product.idExternalChannel)
                this.groups = exists ? buildAssociationGroups([product], this.enumChannel, this.muvenCodes, this.currentId) : []

                // Só uma opção possível (ID simples ou variação já indicada): já deixa escolhida
                if (this.groups.length === 1 && this.groups[0].options.length === 1) {
                    this.selected = this.groups[0].options[0]
                }
            } catch (err) {
                if (seq !== this.requestSeq) {
                    return
                }

                if (!isNotFoundError(err)) {
                    this.resultError = (err && err.message) || 'O canal não respondeu.'
                }
            }

            this.stopLoading()
        },
        toGroups(products, term) {
            if (!this.searchProfile.exactCodeOnly) {
                return buildAssociationGroups(products, this.enumChannel, this.muvenCodes, this.currentId)
            }

            // O agente devolve produtos quaisquer quando não acha: fica só o que tem exatamente o código buscado
            const muven = this.muvenCodes.map((c) => String(c).trim().toLowerCase())

            return keepExactMatches(buildAssociationGroups(products, this.enumChannel, [term], this.currentId))
                .map((group) => ({
                    ...group,
                    options: group.options.map((o) => ({...o, skuMatches: muven.includes(String(o.skuCode || '').trim().toLowerCase())})),
                }))
        },
        startLoading() {
            this.emptyNote = ''
            this.searching = true
            this.hasSearched = false
            this.resultError = ''
            this.groups = []
            this.selected = null
            this.step = 'pick'
            this.saveError = ''
        },
        stopLoading() {
            this.searching = false
            this.hasSearched = true
        },
        retry() {
            if (this.lastAction) {
                this.lastAction()
            }
        },
        choose(option) {
            this.selected = option
            this.step = 'pick'
            this.saveError = ''
        },
        askConfirm(action) {
            if (!this.canSave) {
                return
            }

            this.pendingAction = action || 'swap'

            this.step = 'confirm'
            this.$nextTick(() => this.$refs.confirmButton && this.$refs.confirmButton.focus())
        },
        async persist(idExternalChannel) {
            this.saving = true
            this.saveError = ''

            try {
                await productService.updateProductAssociation(this.ctx.idStore, this.channelId, [{
                    idMuven: this.ctx.idMuven,
                    idExternalChannel,
                }])

                return true
            } catch (err) {
                this.saveError = (err && err.message) || 'Não foi possível salvar a associação.'
                this.step = 'pick'

                return false
            } finally {
                this.saving = false
            }
        },
        isSibling(id) {
            return this.siblingIds.includes(String(id))
        },
        async saveMulti() {
            const action = this.pendingAction
            const id = this.selected.idToSave
            const name = this.ctx.storeChannel.channel.name
            const oldRow = this.ctx.storeChannel.id
            let created

            this.saving = true
            this.saveError = ''

            try {
                const response = await productService.updateProductMultiAssociation(this.ctx.idStore, this.channelId, [{
                    idMuven: this.ctx.idMuven,
                    idExternalChannel: id,
                }])

                created = {idExternalChannel: id, id: findMultiAssociationId(response, id)}
            } catch (err) {
                this.saveError = (err && err.message) || 'Não foi possível salvar a associação.'
                this.step = 'pick'
                this.saving = false
                return
            }

            // Trocar ou preencher o card vazio: a linha antiga sai para não sobrar duplicada
            if (action !== 'add' && oldRow) {
                try {
                    await productService.deleteProductMultiAssociation(oldRow)
                } catch (err) {
                    this.saving = false
                    this.$floatingAlert.error({
                        message: `O anúncio <b>${id}</b> foi associado, mas a associação antiga não foi removida. ${(err && err.message) || ''}`,
                    })
                    await this.finish({action: 'add', ...created})
                    return
                }
            }

            this.saving = false
            this.$floatingAlert.success({
                message: action === 'add'
                    ? `Anúncio <b>${id}</b> adicionado ao produto no canal ${name}.`
                    : `Produto associado ao ID <b>${id}</b> no canal ${name}.`,
            })
            await this.finish({action, ...created})
        },
        async removeMulti() {
            const name = this.ctx.storeChannel.channel.name

            this.saving = true
            this.saveError = ''

            try {
                await productService.deleteProductMultiAssociation(this.ctx.storeChannel.id)
            } catch (err) {
                this.saveError = (err && err.message) || 'Não foi possível remover a associação.'
                this.saving = false
                return
            }

            this.saving = false
            this.$floatingAlert.success({message: `Anúncio <b>${this.currentId}</b> removido do produto no canal ${name}.`})
            await this.finish({action: 'remove', idExternalChannel: null, id: null})
        },
        async save() {
            const id = this.selected.idToSave
            const name = this.ctx.storeChannel.channel.name

            if (await this.persist(id)) {
                this.$floatingAlert.success({message: `Produto associado ao ID <b>${id}</b> no canal ${name}.`})
                await this.finish(id)
            }
        },
        async remove() {
            const name = this.ctx.storeChannel.channel.name

            if (await this.persist(null)) {
                this.$floatingAlert.success({message: `Associação com ${name} removida.`})
                await this.finish(null)
            }
        },
    },
    mounted() {
        // No body: um ancestral com transform (animação dos cards) prenderia o position: fixed do modal
        document.body.appendChild(this.$el)
    },
    beforeDestroy() {
        modalService.hide(`#${this.modalId}`)
    },
    destroyed() {
        if (this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el)
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/product/styles/ChannelAssociationModal.scss';
</style>
